import React, { useCallback, useMemo } from "react";
import {
  Flex,
  TypeaheadInput,
  TextInput,
  Checkbox,
  SearchOption,
  Select,
  DatePicker,
} from "@jsq/ds/core";
import { useArenasData } from "~/pages/DataVault/Entities/hooks/useArenasData";
import { TransactionLookupFilters, TRANSACTION_STATUS } from "../../types";

interface FilterPanelProps {
  filters: TransactionLookupFilters;
  onFilterChange: (filters: Partial<TransactionLookupFilters>) => void;
}

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: TRANSACTION_STATUS.PUBLISHED, label: "Published" },
  { value: TRANSACTION_STATUS.READY_FOR_REVIEW, label: "Ready for Review" },
  { value: TRANSACTION_STATUS.HELD, label: "Held" },
  { value: TRANSACTION_STATUS.TEST, label: "Test" },
  { value: TRANSACTION_STATUS.DELETED, label: "Deleted" },
] as const;

const HAS_MAPPING_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Has Mapping" },
  { value: "false", label: "No Mapping" },
] as const;

export const FilterPanel: React.FC<FilterPanelProps> = ({ filters, onFilterChange }) => {
  const formatDateForFilter = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const parseFilterDate = (value?: string): Date | null => {
    if (!value) return null;
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const { arenas, loading: arenasLoading } = useArenasData();

  const arenaOptions: SearchOption[] = arenas.map((arena) => ({
    id: arena.id,
    label: `${arena.name} (ID: ${arena.id})`,
  }));

  const resolvedArenaLabel = useMemo(() => {
    if (!filters.arenaId || arenas.length === 0) return undefined;
    const match = arenas.find((a) => String(a.id) === filters.arenaId);
    return match ? `${match.name} (ID: ${match.id})` : undefined;
  }, [filters.arenaId, arenas]);

  const handleArenaSelect = useCallback(
    (option: SearchOption) => {
      if (option?.id) {
        onFilterChange({ arenaId: String(option.id) });
      }
    },
    [onFilterChange],
  );

  const handleArenaClear = useCallback(() => {
    onFilterChange({ arenaId: "" });
  }, [onFilterChange]);

  const handleArenaIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value === "" || /^\d+$/.test(value)) {
        onFilterChange({ arenaId: value });
      }
    },
    [onFilterChange],
  );

  const handleEntityIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value === "" || /^\d+$/.test(value)) {
        onFilterChange({ entityId: value === "" ? undefined : parseInt(value, 10) });
      }
    },
    [onFilterChange],
  );

  const handleIncludeDeletedChange = useCallback(() => {
    onFilterChange({ includeDeleted: !filters.includeDeleted });
  }, [filters.includeDeleted, onFilterChange]);

  const handleGlDateFromChange = useCallback(
    (date: Date | null) => {
      onFilterChange({ glDateFrom: date ? formatDateForFilter(date) : undefined });
    },
    [onFilterChange],
  );

  const handleGlDateToChange = useCallback(
    (date: Date | null) => {
      onFilterChange({ glDateTo: date ? formatDateForFilter(date) : undefined });
    },
    [onFilterChange],
  );

  const handleStatusChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const selectedValue = Array.isArray(value) ? value[0] : value;
      onFilterChange({ status: selectedValue ? String(selectedValue) : undefined });
    },
    [onFilterChange],
  );

  const handleBatchSourceObjectIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange({ batchSourceObjectId: event.target.value || undefined });
    },
    [onFilterChange],
  );

  const handleTransactionTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange({ glTransactionType: event.target.value || undefined });
    },
    [onFilterChange],
  );

  const handleHasMappingChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const selectedValue = Array.isArray(value) ? value[0] : value;
      if (selectedValue === "true") {
        onFilterChange({ hasMapping: true });
      } else if (selectedValue === "false") {
        onFilterChange({ hasMapping: false });
      } else {
        onFilterChange({ hasMapping: undefined });
      }
    },
    [onFilterChange],
  );

  const handleEffectiveDateFromChange = useCallback(
    (date: Date | null) => {
      onFilterChange({ effectiveDateFrom: date ? formatDateForFilter(date) : undefined });
    },
    [onFilterChange],
  );

  const handleEffectiveDateToChange = useCallback(
    (date: Date | null) => {
      onFilterChange({ effectiveDateTo: date ? formatDateForFilter(date) : undefined });
    },
    [onFilterChange],
  );

  const handleQuickSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange({ quickSearch: event.target.value || undefined });
    },
    [onFilterChange],
  );

  const labelStyle: React.CSSProperties = {
    fontSize: "14px",
    fontWeight: 500,
    color: "#495057",
    marginBottom: "6px",
  };

  return (
    <Flex
      flexDirection="column"
      style={{
        padding: "20px",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        border: "1px solid #e9ecef",
        gap: "16px",
      }}
    >
      <Flex style={{ gap: "16px", flexWrap: "wrap" }}>
        <Flex flexDirection="column" style={{ flex: "0 0 140px" }}>
          <label htmlFor="arena-id-input" style={labelStyle}>
            JSQ Arena ID <span style={{ color: "#dc3545" }}>*</span>
          </label>
          <TextInput
            id="arena-id-input"
            placeholder="Arena ID"
            value={filters.arenaId}
            onChange={handleArenaIdChange}
            data-testid="arena-id-input"
          />
        </Flex>

        <Flex flexDirection="column" style={{ flex: "1 1 220px", maxWidth: "280px" }}>
          <label htmlFor="arena-selector" style={labelStyle}>
            Arena Name
          </label>
          <TypeaheadInput
            key={resolvedArenaLabel ?? "arena-typeahead"}
            placeholder="Search arenas..."
            options={arenaOptions}
            isLoading={arenasLoading}
            onSelectOption={handleArenaSelect}
            onClearValue={handleArenaClear}
            isClearable
            isFullWidth
            data-testid="arena-selector"
            shouldClearOnSelect={false}
            initialSearchValue={resolvedArenaLabel}
          />
        </Flex>

        <Flex flexDirection="column" style={{ flex: "0 0 120px" }}>
          <label htmlFor="entity-id-input" style={labelStyle}>
            Entity ID
          </label>
          <TextInput
            id="entity-id-input"
            placeholder="Entity ID"
            value={filters.entityId?.toString() ?? ""}
            onChange={handleEntityIdChange}
            data-testid="entity-id-input"
          />
        </Flex>

        <Flex flexDirection="column" style={{ flex: "0 0 160px" }}>
          <Select
            label="Status"
            value={filters.status ?? ""}
            onChange={handleStatusChange}
            placeholder="All Statuses"
            data-testid="status-select"
          >
            <Select.Section>
              {STATUS_OPTIONS.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
        </Flex>
      </Flex>

      <Flex style={{ gap: "16px", flexWrap: "wrap" }}>
        <Flex flexDirection="column" style={{ flex: "0 0 150px" }}>
          <label htmlFor="gl-date-from-input" style={labelStyle}>
            GL Date From
          </label>
          <DatePicker
            selected={parseFilterDate(filters.glDateFrom)}
            onChange={handleGlDateFromChange}
            dateFormat="MM/dd/yyyy"
            hasIcon
            placeholderText="Select date"
            data-testid="gl-date-from-input"
          />
        </Flex>

        <Flex flexDirection="column" style={{ flex: "0 0 150px" }}>
          <label htmlFor="gl-date-to-input" style={labelStyle}>
            GL Date To
          </label>
          <DatePicker
            selected={parseFilterDate(filters.glDateTo)}
            onChange={handleGlDateToChange}
            dateFormat="MM/dd/yyyy"
            hasIcon
            placeholderText="Select date"
            data-testid="gl-date-to-input"
          />
        </Flex>

        <Flex flexDirection="column" style={{ flex: "0 0 150px" }}>
          <label htmlFor="effective-date-from-input" style={labelStyle}>
            Effective Date From
          </label>
          <DatePicker
            id="effective-date-from-input"
            selected={parseFilterDate(filters.effectiveDateFrom)}
            onChange={handleEffectiveDateFromChange}
            dateFormat="MM/dd/yyyy"
            hasIcon
            placeholderText="Select date"
            data-testid="effective-date-from-input"
          />
        </Flex>

        <Flex flexDirection="column" style={{ flex: "0 0 150px" }}>
          <label htmlFor="effective-date-to-input" style={labelStyle}>
            Effective Date To
          </label>
          <DatePicker
            id="effective-date-to-input"
            selected={parseFilterDate(filters.effectiveDateTo)}
            onChange={handleEffectiveDateToChange}
            dateFormat="MM/dd/yyyy"
            hasIcon
            placeholderText="Select date"
            data-testid="effective-date-to-input"
          />
        </Flex>
      </Flex>

      <Flex style={{ gap: "16px", alignItems: "flex-end", flexWrap: "wrap" }}>
        <Flex flexDirection="column" style={{ flex: "1 1 220px" }}>
          <label htmlFor="quick-search-input" style={labelStyle}>
            Quick Search
          </label>
          <TextInput
            id="quick-search-input"
            placeholder="Description, vendor, invoice, batch..."
            value={filters.quickSearch ?? ""}
            onChange={handleQuickSearchChange}
            data-testid="quick-search-input"
          />
        </Flex>

        <Flex flexDirection="column" style={{ flex: "0 0 220px" }}>
          <label htmlFor="batch-id-input" style={labelStyle}>
            GL Batch ID
          </label>
          <TextInput
            id="batch-id-input"
            placeholder="GL Batch ID"
            value={filters.batchSourceObjectId ?? ""}
            onChange={handleBatchSourceObjectIdChange}
            data-testid="batch-id-input"
          />
        </Flex>

        <Flex flexDirection="column" style={{ flex: "0 0 220px" }}>
          <label htmlFor="transaction-type-input" style={labelStyle}>
            GL Transaction Type
          </label>
          <TextInput
            id="transaction-type-input"
            placeholder="e.g. Capital Call"
            value={filters.glTransactionType ?? ""}
            onChange={handleTransactionTypeChange}
            data-testid="transaction-type-input"
          />
        </Flex>

        <Flex flexDirection="column" style={{ flex: "0 0 170px" }}>
          <Select
            label="Mapping"
            value={
              filters.hasMapping === undefined ? "" : filters.hasMapping ? "true" : "false"
            }
            onChange={handleHasMappingChange}
            placeholder="All"
            data-testid="has-mapping-select"
          >
            <Select.Section>
              {HAS_MAPPING_OPTIONS.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
        </Flex>

        <Flex style={{ paddingBottom: "8px", flexShrink: 0 }}>
          <Checkbox
            name="include-deleted-checkbox"
            label="Include Deleted"
            checked={filters.includeDeleted}
            onChange={handleIncludeDeletedChange}
            data-testid="include-deleted-checkbox"
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
